import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { ApiError } from '../../services/apiClient';
import spottoLogo from '../../assets/spotto-logo.png';

export const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    // Basic validation
    if (!email) {
      setError(t('auth.validation.emailRequired'));
      return;
    }

    if (!email.includes('@')) {
      setError(t('auth.validation.invalidEmail'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.forgotPassword({ email });
      setSuccessMessage(response.message);
      setEmail(''); // Clear the form
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || t('auth.validation.unexpectedError'));
      } else {
        setError(t('auth.validation.unexpectedError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#23262b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img
              src={spottoLogo}
              alt="Spotto Logo"
              className="h-20 w-auto"
              style={{
                filter:
                  "drop-shadow(0 6px 14px rgba(0, 0, 0, 0.45)) drop-shadow(0 0 28px rgba(77, 99, 172, 0.50))",
              }}
            />
          </div>
          <h1
            className="font-display text-3xl font-semibold text-white mb-2"
            style={{
              textShadow: "0 2px 10px rgba(0, 0, 0, 0.45)",
              letterSpacing: "-0.02em",
            }}
          >
            {t('app.name')}
          </h1>
          <p className="text-[#9aa3b2]">{t('app.subtitle')}</p>
        </div>

        {/* Forgot Password Form */}
        <div className="bg-[#2c3036] rounded-2xl border border-white/[0.08] p-8 shadow-xl">
          <h2 className="text-2xl font-bold text-white mb-2">{t('auth.forgotPassword')}</h2>
          <p className="text-sm text-[#9aa3b2] mb-6">
            {t('auth.forgotPasswordDescription')}
          </p>

          {error && (
            <div className="mb-6 p-4 bg-[#c06f57]/10 border border-[#c06f57]/30 rounded-lg">
              <p className="text-sm text-[#e1a08a]">{error}</p>
            </div>
          )}

          {successMessage && (
            <div className="mb-6 p-4 bg-[#5fa78a]/10 border border-[#5fa78a]/30 rounded-lg">
              <p className="text-sm text-[#86c5ad]">{successMessage}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-[#c5cde0] mb-2">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#2a2e34] border border-white/[0.10] rounded-lg text-white placeholder-[#6b7280] focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:border-transparent transition-all"
                placeholder={t('auth.emailPlaceholder')}
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-[#4d63ac] hover:bg-[#5b71be] disabled:bg-[#4d63ac]/50 text-white font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#4d63ac] focus:ring-offset-2 focus:ring-offset-[#2c3036]"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('auth.sending')}
                </span>
              ) : (
                t('auth.sendResetLink')
              )}
            </button>
          </form>

          {/* Back to Login Link */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-sm text-[#9aa3b2] hover:text-[#c5cde0] transition-colors inline-flex items-center"
            >
              <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('auth.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
